import type { MutableRefObject } from 'react'
import { postConfirmChunkUpload, postUploadChunk } from '../api/voice-bot-client'
import type { VoiceSessionData } from '../types'

export interface FlushChunkBufferDeps {
  chunkBufferRef: MutableRefObject<Blob[]>
  chunkIndexRef: MutableRefObject<number>
  sessionDataRef: MutableRefObject<VoiceSessionData | null>
  publicKey?: string
}

/**
 * Flush accumulated audio chunks to Supabase Storage via presigned URL.
 * Called every CHUNK_FLUSH_INTERVAL ondataavailable events (~10s).
 * Non-blocking — upload happens in background, doesn't affect call quality.
 */
export const flushChunkBuffer = async (
  deps: FlushChunkBufferDeps,
): Promise<boolean> => {
  const { chunkBufferRef, chunkIndexRef, sessionDataRef, publicKey } = deps

  const chunks = chunkBufferRef.current.slice()
  chunkBufferRef.current = []
  if (chunks.length === 0) return true

  const currentIndex = chunkIndexRef.current
  chunkIndexRef.current++

  try {
    const urlResponse = await postUploadChunk(
      {
        sessionId: sessionDataRef.current?.sessionId,
        chunkIndex: currentIndex,
      },
      publicKey,
    )

    if (!urlResponse.ok) {
      console.warn(`⚠️ Chunk ${currentIndex}: Failed to get upload URL (${urlResponse.status})`)
      return false
    }

    const { uploadUrl } = await urlResponse.json()

    const chunkBlob = new Blob(chunks, { type: 'audio/webm' })
    const uploadResponse = await fetch(uploadUrl, {
      method: 'PUT',
      body: chunkBlob,
      headers: {
        'Content-Type': 'audio/webm',
        'x-upsert': 'false',
      },
    })

    if (!uploadResponse.ok) {
      console.warn(`⚠️ Chunk ${currentIndex}: Upload failed (${uploadResponse.status})`)
      return false
    }

    let confirmed = false
    for (const delay of [0, 250, 750]) {
      if (delay) await new Promise(resolve => setTimeout(resolve, delay))
      const confirmResponse = await postConfirmChunkUpload(
        {
          sessionId: sessionDataRef.current?.sessionId,
          chunkIndex: currentIndex,
        },
        publicKey,
      )
      if (confirmResponse.ok) {
        confirmed = true
        break
      }
      console.warn(`⚠️ Chunk ${currentIndex}: Recovery confirmation failed (${confirmResponse.status})`)
    }
    if (!confirmed) {
      console.warn(`⚠️ Chunk ${currentIndex}: Recovery confirmation exhausted retries`)
      return false
    }

    console.log(`📦 Chunk ${currentIndex} uploaded (${(chunkBlob.size / 1024).toFixed(1)} KB)`)
    return true
  } catch (err) {
    console.warn(`⚠️ Chunk ${currentIndex} upload error:`, err)
    return false
  }
}
